import {
  OFFER_STATUS,
  OFFER_STATUS_LABELS,
  OFFER_STATUS_COLORS,
  OFFER_STATUS_TRANSITIONS,
  DEFAULT_CURRENCY,
  DEFAULT_QUANTITY
} from "../constants/offer.constants.js";


/*
|--------------------------------------------------------------------------
| NUMBER
|--------------------------------------------------------------------------
*/

export const toNumber = (
  value,
  defaultValue = 0
) => {
  const number = Number(value);

  if (
    Number.isNaN(number)
  ) {
    return defaultValue;
  }

  return number;
};


/*
|--------------------------------------------------------------------------
| ROUND MONEY
|--------------------------------------------------------------------------
*/

export const roundMoney = (
  value
) => {
  return (
    Math.round(
      (
        Number(value) +
        Number.EPSILON
      ) * 100
    ) / 100
  );
};


/*
|--------------------------------------------------------------------------
| FORMAT MONEY
|--------------------------------------------------------------------------
*/

export const formatMoney = (
  value,
  currency = DEFAULT_CURRENCY
) => {
  const amount =
    toNumber(value);


  try {
    return new Intl.NumberFormat(
      "en-IN",
      {
        style: "currency",
        currency: currency || DEFAULT_CURRENCY,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    ).format(amount);
  } catch {
    return `${currency || DEFAULT_CURRENCY} ${amount.toFixed(2)}`;
  }
};


/*
|--------------------------------------------------------------------------
| FORMAT NUMBER
|--------------------------------------------------------------------------
*/

export const formatNumber = (
  value
) => {
  return new Intl.NumberFormat(
    "en-IN"
  ).format(
    toNumber(value)
  );
};


/*
|--------------------------------------------------------------------------
| FORMAT DATE
|--------------------------------------------------------------------------
*/

export const formatOfferDate = (
  value
) => {
  if (!value) {
    return "-";
  }


  const date =
    new Date(value);


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }


  return new Intl.DateTimeFormat(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }
  ).format(date);
};


/*
|--------------------------------------------------------------------------
| FORMAT DATETIME
|--------------------------------------------------------------------------
*/

export const formatOfferDateTime = (
  value
) => {
  if (!value) {
    return "-";
  }


  const date =
    new Date(value);


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }


  return new Intl.DateTimeFormat(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }
  ).format(date);
};


/*
|--------------------------------------------------------------------------
| STATUS
|--------------------------------------------------------------------------
*/

export const getOfferStatusLabel = (
  status
) => {
  return (
    OFFER_STATUS_LABELS[status] ||
    status ||
    "-"
  );
};


export const getOfferStatusColor = (
  status
) => {
  return (
    OFFER_STATUS_COLORS[status] ||
    "gray"
  );
};


/*
|--------------------------------------------------------------------------
| STATUS TRANSITIONS
|--------------------------------------------------------------------------
*/

export const getAvailableOfferStatuses = (
  currentStatus
) => {
  return (
    OFFER_STATUS_TRANSITIONS[
      currentStatus
    ] || []
  );
};


export const canChangeOfferStatus = (
  currentStatus,
  nextStatus
) => {
  return getAvailableOfferStatuses(
    currentStatus
  ).includes(
    nextStatus
  );
};


/*
|--------------------------------------------------------------------------
| ITEM TOTAL
|--------------------------------------------------------------------------
*/

export const calculateItemTotal = ({
  quantity = DEFAULT_QUANTITY,
  unitPrice = 0,
  discountAmount = 0,
  taxAmount = 0
}) => {
  const qty =
    toNumber(
      quantity,
      DEFAULT_QUANTITY
    );

  const price =
    toNumber(unitPrice);

  const discount =
    toNumber(discountAmount);

  const tax =
    toNumber(taxAmount);


  const subtotal =
    roundMoney(
      qty * price
    );


  const total =
    roundMoney(
      subtotal -
      discount +
      tax
    );


  return {
    subtotal,
    discountAmount: discount,
    taxAmount: tax,
    totalAmount: Math.max(
      total,
      0
    )
  };
};


/*
|--------------------------------------------------------------------------
| OFFER TOTALS
|--------------------------------------------------------------------------
*/

export const calculateOfferTotals = (
  items = [],
  discountAmount = 0,
  taxAmount = 0
) => {
  let subtotal = 0;
  let itemDiscount = 0;
  let itemTax = 0;


  for (
    const item of items
  ) {
    const itemTotal =
      calculateItemTotal({
        quantity:
          item.quantity,

        unitPrice:
          item.unitPrice,

        discountAmount:
          item.discountAmount,

        taxAmount:
          item.taxAmount
      });


    subtotal +=
      itemTotal.subtotal;

    itemDiscount +=
      toNumber(
        item.discountAmount
      );

    itemTax +=
      toNumber(
        item.taxAmount
      );
  }


  subtotal =
    roundMoney(
      subtotal
    );


  const offerDiscount =
    toNumber(
      discountAmount
    );


  const offerTax =
    toNumber(
      taxAmount
    );


  const totalDiscount =
    roundMoney(
      itemDiscount +
      offerDiscount
    );


  const totalTax =
    roundMoney(
      itemTax +
      offerTax
    );


  const totalAmount =
    roundMoney(
      subtotal -
      totalDiscount +
      totalTax
    );


  return {
    subtotal,
    discountAmount:
      totalDiscount,
    taxAmount:
      totalTax,
    totalAmount:
      Math.max(
        totalAmount,
        0
      )
  };
};


/*
|--------------------------------------------------------------------------
| OFFER ITEM DISPLAY TOTAL
|--------------------------------------------------------------------------
*/

export const getOfferItemTotal = (
  item
) => {
  return calculateItemTotal({
    quantity:
      item.quantity,

    unitPrice:
      item.unit_price ??
      item.unitPrice,

    discountAmount:
      item.discount_amount ??
      item.discountAmount,

    taxAmount:
      item.tax_amount ??
      item.taxAmount
  }).totalAmount;
};


/*
|--------------------------------------------------------------------------
| OFFER STATUS HELPERS
|--------------------------------------------------------------------------
*/

export const isDraftOffer = (
  offer
) => {
  return (
    offer?.status ===
    OFFER_STATUS.DRAFT
  );
};


export const isSentOffer = (
  offer
) => {
  return (
    offer?.status ===
    OFFER_STATUS.SENT
  );
};


export const isAcceptedOffer = (
  offer
) => {
  return (
    offer?.status ===
    OFFER_STATUS.ACCEPTED
  );
};


export const isTerminalOffer = (
  offer
) => {
  return [
    OFFER_STATUS.ACCEPTED,
    OFFER_STATUS.REJECTED,
    OFFER_STATUS.EXPIRED,
    OFFER_STATUS.CANCELLED
  ].includes(
    offer?.status
  );
};


/*
|--------------------------------------------------------------------------
| EDITABLE
|--------------------------------------------------------------------------
*/

export const canEditOffer = (
  offer
) => {
  return isDraftOffer(
    offer
  );
};


export const canDeleteOffer = (
  offer
) => {
  return isDraftOffer(
    offer
  );
};


export const canManageOfferItems = (
  offer
) => {
  return isDraftOffer(
    offer
  );
};


/*
|--------------------------------------------------------------------------
| SEND VALIDATION
|--------------------------------------------------------------------------
*/

export const getOfferSendValidationError = (
  offer
) => {
  if (!offer) {
    return "Offer not found";
  }


  if (
    !offer.valid_until
  ) {
    return (
      "Valid until is required before sending the offer"
    );
  }


  if (
    Number(
      offer.total_amount
    ) <= 0
  ) {
    return (
      "Cannot send an offer with zero total amount"
    );
  }


  return null;
};


/*
|--------------------------------------------------------------------------
| OFFER NUMBER
|--------------------------------------------------------------------------
*/

export const isValidOfferNumber = (
  offerNumber
) => {
  if (
    typeof offerNumber !== "string"
  ) {
    return false;
  }


  return /^OFF-\d{8}-[A-F0-9]+$/i.test(
    offerNumber
  );
};


/*
|--------------------------------------------------------------------------
| NORMALIZE OFFER ITEM
|--------------------------------------------------------------------------
|
| Useful because backend returns snake_case while
| forms normally use camelCase.
|
*/

export const normalizeOfferItem = (
  item = {}
) => {
  return {
    id:
      item.id || null,

    offerId:
      item.offer_id ??
      item.offerId ??
      null,

    offeringId:
      item.offering_id ??
      item.offeringId ??
      "",

    pricingPlanId:
      item.pricing_plan_id ??
      item.pricingPlanId ??
      "",

    description:
      item.description || "",

    quantity:
      item.quantity ??
      DEFAULT_QUANTITY,

    unitPrice:
      item.unit_price ??
      item.unitPrice ??
      "",

    discountAmount:
      item.discount_amount ??
      item.discountAmount ??
      0,

    taxAmount:
      item.tax_amount ??
      item.taxAmount ??
      0,

    totalAmount:
      item.total_amount ??
      item.totalAmount ??
      0,

    offeringName:
      item.offering_name ??
      "",

    offeringSlug:
      item.offering_slug ??
      "",

    pricingPlanName:
      item.pricing_plan_name ??
      "",

    pricingType:
      item.pricing_type ??
      "",

    pricingPlanPrice:
      item.pricing_plan_price ??
      null,

    pricingPlanCurrency:
      item.pricing_plan_currency ??
      ""
  };
};


/*
|--------------------------------------------------------------------------
| NORMALIZE OFFER
|--------------------------------------------------------------------------
*/

export const normalizeOffer = (
  offer = {}
) => {
  return {
    ...offer,

    queryId:
      offer.query_id ??
      offer.queryId ??
      null,

    clientId:
      offer.client_id ??
      offer.clientId ??
      null,

    offerNumber:
      offer.offer_number ??
      offer.offerNumber ??
      "",

    discountAmount:
      offer.discount_amount ??
      offer.discountAmount ??
      0,

    taxAmount:
      offer.tax_amount ??
      offer.taxAmount ??
      0,

    totalAmount:
      offer.total_amount ??
      offer.totalAmount ??
      0,

    validUntil:
      offer.valid_until ??
      offer.validUntil ??
      null,

    createdBy:
      offer.created_by ??
      offer.createdBy ??
      null,

    acceptedAt:
      offer.accepted_at ??
      offer.acceptedAt ??
      null,

    items:
      Array.isArray(offer.items)
        ? offer.items.map(
            normalizeOfferItem
          )
        : []
  };
};


/*
|--------------------------------------------------------------------------
| BUILD OFFER PAYLOAD
|--------------------------------------------------------------------------
*/

export const buildOfferPayload = (
  formData
) => {
  return {
    queryId:
      formData.queryId,

    currency:
      (
        formData.currency ||
        DEFAULT_CURRENCY
      ).toUpperCase(),

    discountAmount:
      toNumber(
        formData.discountAmount
      ),

    taxAmount:
      toNumber(
        formData.taxAmount
      ),

    validUntil:
      formData.validUntil ||
      null,

    notes:
      formData.notes ||
      null,

    items:
      (formData.items || [])
        .map((item) => ({
          offeringId:
            item.offeringId ||
            null,

          pricingPlanId:
            item.pricingPlanId ||
            null,

          description:
            item.description?.trim() ||
            "",

          quantity:
            toNumber(
              item.quantity,
              DEFAULT_QUANTITY
            ),

          unitPrice:
            toNumber(
              item.unitPrice
            ),

          discountAmount:
            toNumber(
              item.discountAmount
            ),

          taxAmount:
            toNumber(
              item.taxAmount
            )
        }))
  };
};


/*
|--------------------------------------------------------------------------
| BUILD UPDATE PAYLOAD
|--------------------------------------------------------------------------
*/

export const buildOfferUpdatePayload = (
  formData
) => {
  return {
    discountAmount:
      formData.discountAmount !==
      undefined
        ? toNumber(
            formData.discountAmount
          )
        : undefined,

    taxAmount:
      formData.taxAmount !==
      undefined
        ? toNumber(
            formData.taxAmount
          )
        : undefined,

    currency:
      formData.currency
        ? formData.currency.toUpperCase()
        : undefined,

    validUntil:
      formData.validUntil,

    notes:
      formData.notes
  };
};


/*
|--------------------------------------------------------------------------
| BUILD ITEM PAYLOAD
|--------------------------------------------------------------------------
*/

export const buildOfferItemPayload = (
  item
) => {
  return {
    offeringId:
      item.offeringId ||
      null,

    pricingPlanId:
      item.pricingPlanId ||
      null,

    description:
      item.description?.trim() ||
      "",

    quantity:
      toNumber(
        item.quantity,
        DEFAULT_QUANTITY
      ),

    unitPrice:
      item.unitPrice !== ""
        ? toNumber(
            item.unitPrice
          )
        : undefined,

    discountAmount:
      toNumber(
        item.discountAmount
      ),

    taxAmount:
      toNumber(
        item.taxAmount
      )
  };
};